import React, { useEffect, useState } from 'react';
import { useParams, useOutletContext } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supabase } from '../../lib/supabase';
import { Loader2, Search, Trophy, Crown } from 'lucide-react';
import { SponsorBanner } from '../../components/ui';
import { isDemoTournament, getDemoTournament, getDemoTeams, getDemoMatches, getDemoPoules } from '../../data/demoData';

const PublicStandingsPage = () => {
    const { t } = useTranslation();
    const { id: tournamentId } = useParams();
    const outletContext = useOutletContext();
    const isEsports = outletContext?.isEsports || false;

    const [standings, setStandings] = useState({});
    const [loading, setLoading] = useState(true);
    const [poules, setPoules] = useState([]);
    const [teams, setTeams] = useState([]);
    const [allTeams, setAllTeams] = useState([]); // Flat list for dropdown
    const [sponsorImages, setSponsorImages] = useState([]);
    const [sportType, setSportType] = useState('goals');

    // Initialize from local storage
    const [selectedTeam, setSelectedTeam] = useState(() => localStorage.getItem('propoule_fav_team') || 'all');

    useEffect(() => {
        fetchData();

        const subscription = supabase
            .channel('public_standings')
            .on('postgres_changes', {
                event: '*',
                schema: 'public',
                table: 'matches',
                filter: `tournament_id=eq.${tournamentId}`
            }, () => {
                fetchData(false);
            })
            .subscribe();

        return () => {
            subscription.unsubscribe();
        };
    }, [tournamentId]);

    // Save selection to local storage
    useEffect(() => {
        if (selectedTeam) {
            localStorage.setItem('propoule_fav_team', selectedTeam);
        }
    }, [selectedTeam]);

    const fetchData = async (showLoading = true) => {
        if (showLoading) setLoading(true);

        // --- DEMO INTERCEPT ---
        if (isDemoTournament(tournamentId)) {
            const dTourney = getDemoTournament(tournamentId);
            const dPoules = getDemoPoules(tournamentId);
            const dTeams = getDemoTeams(tournamentId);
            const dMatches = getDemoMatches(tournamentId);

            setSponsorImages([dTourney.sponsor_left_image, dTourney.sponsor_right_image].filter(Boolean));
            setPoules(dPoules);
            setAllTeams(dTeams);

            // Calc Standings from Demo Data
            const newStandings = {};
            dPoules.forEach(poule => {
                const pouleTeams = dTeams.filter(t => t.poule_id === poule.id);
                const pouleMatches = dMatches.filter(m => m.poule_id === poule.id);

                const stats = pouleTeams.map(team => {
                    let played = 0, won = 0, draw = 0, lost = 0, gf = 0, ga = 0, points = 0;
                    pouleMatches.forEach(match => {
                        const scoreHome = match.score_home;
                        const scoreAway = match.score_away;
                        if (scoreHome !== null && scoreAway !== null) {
                            const home = Number(scoreHome);
                            const away = Number(scoreAway);
                            if (match.home_team_id === team.id) {
                                played++; gf += home; ga += away;
                                if (home > away) { won++; points += 3; }
                                else if (home === away) { draw++; points += 1; }
                                else { lost++; }
                            } else if (match.away_team_id === team.id) {
                                played++; gf += away; ga += home;
                                if (away > home) { won++; points += 3; }
                                else if (away === home) { draw++; points += 1; }
                                else { lost++; }
                            }
                        }
                    });
                    return { ...team, played, won, draw, lost, gf, ga, points, gd: gf - ga };
                });
                // Sort
                stats.sort((a, b) => (b.points - a.points) || (b.gd - a.gd) || (b.gf - a.gf));
                newStandings[poule.id] = stats;
            });

            setStandings(newStandings);
            setLoading(false);
            return;
        }
        // --- END DEMO INTERCEPT ---

        try {
            // Tournament info for sponsors
            const { data: tournamentData } = await supabase
                .from('tournaments')
                .select('sponsor_left_image, sponsor_right_image, sport_type')
                .eq('id', tournamentId)
                .single();

            if (tournamentData) {
                const sponsors = [];
                if (tournamentData.sponsor_left_image) sponsors.push(...tournamentData.sponsor_left_image.split(','));
                if (tournamentData.sponsor_right_image) sponsors.push(...tournamentData.sponsor_right_image.split(','));
                setSponsorImages(sponsors.filter(Boolean));
                setSportType(tournamentData.sport_type || 'goals');
            }

            // Poules
            const { data: poulesData } = await supabase
                .from('poules')
                .select('id, name')
                .eq('tournament_id', tournamentId)
                .order('name');

            if (poulesData) setPoules(poulesData);

            // Teams
            const { data: teamsData } = await supabase
                .from('teams')
                .select('id, name, logo_url, poule_id')
                .eq('tournament_id', tournamentId)
                .order('name');

            setAllTeams(teamsData || []);
            const allTeamsList = teamsData || [];

            // Matches (Fetch set_scores too!)
            const { data: matchesData } = await supabase
                .from('matches')
                .select('home_team_id, away_team_id, score_home, score_away, poule_id, set_scores')
                .eq('tournament_id', tournamentId);

            // Calculation Logic
            const newStandings = {};
            const matches = matchesData || [];

            poulesData?.forEach(poule => {
                const pouleTeams = allTeamsList.filter(t => t.poule_id === poule.id);
                // Filter matches for this poule
                const pouleMatches = matches.filter(m => m.poule_id === poule.id);

                const stats = pouleTeams.map(team => {
                    let played = 0, won = 0, draw = 0, lost = 0, gf = 0, ga = 0, points = 0;
                    let gamesFor = 0, gamesAgainst = 0;

                    pouleMatches.forEach(match => {
                        // Ensure scores are numbers
                        const scoreHome = match.score_home;
                        const scoreAway = match.score_away;

                        // Check if scores exist (not null)
                        if (scoreHome !== null && scoreAway !== null) {
                            const home = Number(scoreHome);
                            const away = Number(scoreAway);

                            if (match.home_team_id === team.id) {
                                played++;
                                gf += home; // Sets won
                                ga += away; // Sets lost
                                if (home > away) { won++; points += 3; }
                                else if (home === away) { draw++; points += 1; }
                                else { lost++; }

                                if (match.set_scores && Array.isArray(match.set_scores)) {
                                    match.set_scores.forEach(s => {
                                        gamesFor += parseInt(s.home || 0);
                                        gamesAgainst += parseInt(s.away || 0);
                                    });
                                }
                            } else if (match.away_team_id === team.id) {
                                played++;
                                gf += away; // Sets won
                                ga += home; // Sets lost
                                if (away > home) { won++; points += 3; }
                                else if (away === home) { draw++; points += 1; }
                                else { lost++; }

                                if (match.set_scores && Array.isArray(match.set_scores)) {
                                    match.set_scores.forEach(s => {
                                        gamesFor += parseInt(s.away || 0);
                                        gamesAgainst += parseInt(s.home || 0);
                                    });
                                }
                            }
                        }
                    });

                    return {
                        ...team,
                        played, won, draw, lost, gf, ga, points,
                        gd: gf - ga, // Sets difference
                        gamesFor, gamesAgainst,
                        gamesDifference: gamesFor - gamesAgainst
                    };
                });

                // Sort: Points > Sets Difference > Games Difference > Sets Won (for racket sports)
                stats.sort((a, b) => {
                    if (b.points !== a.points) return b.points - a.points;
                    if (sportType === 'sets') {
                        // 1. Sets Difference
                        const aSetsDiff = a.gf - a.ga;
                        const bSetsDiff = b.gf - b.ga;
                        if (bSetsDiff !== aSetsDiff) return bSetsDiff - aSetsDiff;

                        // 2. Games Difference
                        if (b.gamesDifference !== a.gamesDifference) return b.gamesDifference - a.gamesDifference;

                        // 3. Sets Won
                        return b.gf - a.gf;
                    } else {
                        // Standard Goals sorting: 1. GD, 2. GF
                        if (b.gd !== a.gd) return b.gd - a.gd;
                        return b.gf - a.gf;
                    }
                });

                newStandings[poule.id] = stats;
            });

            setStandings(newStandings);
        } catch (err) {
            console.error('Error calculating standings:', err);
        } finally {
            if (showLoading) setLoading(false);
        }
    };

    // Filter Logic for Display
    let displayedPoules = poules;
    if (selectedTeam !== 'all') {
        // Find which poule the selected team belongs to
        const teamObj = allTeams.find(t => t.id === selectedTeam);
        if (teamObj && teamObj.poule_id) {
            displayedPoules = poules.filter(p => p.id === teamObj.poule_id);
        }
    }

    if (loading) return (
        <div className="flex justify-center p-8">
            <Loader2 className="animate-spin text-white" />
        </div>
    );

    return (
        <div className="p-4 space-y-6 pb-24 max-w-3xl mx-auto font-sans">
            {/* Team Filter (Top) */}
            <div className={`relative sticky top-0 z-30 pt-2 transition-colors ${isEsports ? 'bg-[#08080f]' : 'bg-white'}`}>
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Search size={16} className={isEsports ? 'text-purple-400' : 'text-gray-400'} />
                </div>
                <select
                    value={selectedTeam}
                    onChange={(e) => setSelectedTeam(e.target.value)}
                    className={`block w-full pl-10 pr-4 py-3 rounded-xl shadow-lg focus:ring-2 focus:ring-purple-500 appearance-none font-semibold text-sm transition-all ${
                        isEsports 
                            ? 'bg-[#0b0b14]/95 text-white border border-purple-500/20' 
                            : 'bg-white/95 text-gray-900 border-none'
                    }`}
                >
                    <option value="all" className={isEsports ? 'bg-[#0b0b14] text-white' : ''}>🔍 {t('public.search_placeholder')}</option>
                    {allTeams.map(team => (
                        <option key={team.id} value={team.id} className={isEsports ? 'bg-[#0b0b14] text-white' : ''}>{team.name}</option>
                    ))}
                </select>
            </div>

            {selectedTeam !== 'all' && (
                <div className={`text-xs font-bold text-center px-4 py-1.5 rounded-full mx-auto w-fit backdrop-blur-sm animate-fade-in ${
                    isEsports ? 'bg-purple-600/40 text-purple-200 border border-purple-500/30' : 'bg-indigo-600/80 text-white'
                }`}>
                    {t('public.your_ranking')}:
                    <button onClick={() => setSelectedTeam('all')} className="ml-2 underline text-white/85 hover:text-white">{t('public.show_all')}</button>
                </div>
            )}

            {displayedPoules.length === 0 && (
                <div className={`text-center py-10 rounded border shadow-lg flex flex-col items-center justify-center gap-3 transition-colors ${
                    isEsports 
                        ? 'bg-[#0a0a14]/90 border-purple-500/20 text-slate-400 shadow-[0_0_20px_rgba(168,85,247,0.05)]' 
                        : 'bg-white/90 border-white/20 text-gray-500'
                }`}>
                    <Trophy size={40} className={isEsports ? 'text-purple-400/50 animate-pulse' : 'text-gray-300'} />
                    <span className="font-bold text-sm tracking-wide">{t('public.no_poules_found')}</span>
                </div>
            )}

            {displayedPoules.map(poule => (
                <div key={poule.id} className={`rounded-xl shadow-lg border overflow-hidden animate-slide-up transition-all duration-300 ${
                    isEsports 
                        ? 'bg-[#0a0a14]/90 border-purple-500/20 shadow-[0_0_30px_rgba(168,85,247,0.05)]' 
                        : 'bg-white/90 border-white/20'
                }`}>
                    <div className={`px-4 py-3.5 font-bold flex justify-between items-center backdrop-blur-sm transition-colors border-b ${
                        isEsports 
                            ? 'bg-gradient-to-r from-purple-950 to-indigo-950 text-white border-purple-500/25' 
                            : 'bg-gray-900/90 text-white border-gray-100'
                    }`}>
                        <span className={`tracking-wider font-mono uppercase text-sm ${isEsports ? 'text-cyan-400 drop-shadow-[0_0_8px_rgba(6,182,212,0.4)]' : ''}`}>{poule.name}</span>
                    </div>

                    <div className="overflow-x-auto">
                        <table className="w-full text-sm text-left">
                            <thead className={`text-xs uppercase border-b transition-colors ${
                                isEsports 
                                    ? 'bg-[#0b0b14] border-purple-500/10 text-cyan-400/80 font-mono text-[10px]' 
                                    : 'text-gray-500 bg-gray-50/50 border-gray-100'
                            }`}>
                                <tr>
                                    <th className="px-3 py-3 w-8 text-center">{t('standings_page.table_header_rank')}</th>
                                    <th className="px-3 py-3">{t('standings_page.table_header_team')}</th>
                                    <th className="px-2 py-3 text-center w-8" title="Gespeeld">{t('presentation_page.tv_played', 'GS')}</th>
                                    <th className="px-2 py-3 text-center w-8">{t('standings_page.table_header_win')}</th>
                                    {sportType !== 'sets' && <th className="px-2 py-3 text-center w-8">{t('standings_page.table_header_draw')}</th>}
                                    <th className="px-2 py-3 text-center w-8">{t('standings_page.table_header_loss')}</th>
                                    <th className="px-2 py-3 text-center w-10">{sportType === 'sets' ? 'SV' : 'DV'}</th>
                                    <th className="px-2 py-3 text-center w-10">{sportType === 'sets' ? 'ST' : 'DT'}</th>
                                    <th className="px-2 py-3 text-center w-10 font-bold">{sportType === 'sets' ? 'Saldo' : t('standings_page.table_header_goals')}</th>
                                    <th className={`px-3 py-3 text-right w-12 font-bold ${isEsports ? 'text-white' : 'text-gray-900'}`}>{t('standings_page.table_header_points')}</th>
                                </tr>
                            </thead>
                            <tbody className={`divide-y transition-colors ${isEsports ? 'divide-purple-500/10' : 'divide-gray-100'}`}>
                                {standings[poule.id]?.map((team, index) => {
                                    const isHighlighted = team.id === selectedTeam;
                                    return (
                                        <tr key={team.id} className={`transition-all ${
                                            isEsports 
                                                ? (isHighlighted 
                                                    ? 'bg-purple-900/20 border-l-4 border-purple-500 text-white font-bold' 
                                                    : (index < 2 ? 'bg-purple-950/5 text-slate-200' : 'text-slate-300 hover:bg-purple-500/5'))
                                                : (isHighlighted 
                                                    ? 'bg-indigo-100 border-l-4 border-indigo-600 text-indigo-900' 
                                                    : (index < 2 ? 'bg-indigo-50/20 text-gray-800' : 'text-gray-700 hover:bg-indigo-50/50'))
                                        }`}>
                                            <td className={`px-3 py-3.5 text-center font-bold font-mono ${
                                                isHighlighted 
                                                    ? (isEsports ? 'text-purple-300' : 'text-indigo-700') 
                                                    : 'text-gray-400'
                                            }`}>
                                                <div className="flex items-center justify-center gap-1">
                                                    {index === 0 && (
                                                        <Crown size={12} className={isEsports ? 'text-yellow-400 drop-shadow-[0_0_6px_rgba(234,179,8,0.6)] animate-pulse' : 'text-yellow-500'} />
                                                    )}
                                                    {index === 1 && (
                                                        <Crown size={12} className={isEsports ? 'text-slate-300 drop-shadow-[0_0_4px_rgba(203,213,225,0.4)]' : 'text-slate-400'} />
                                                    )}
                                                    <span>{index + 1}</span>
                                                </div>
                                            </td>
                                            <td className="px-3 py-3.5 font-semibold">
                                                <div className="flex items-center gap-2.5 truncate max-w-[170px]">
                                                    {team.logo_url && (
                                                        <img 
                                                            src={team.logo_url} 
                                                            className={`w-6 h-6 rounded object-contain shrink-0 p-0.5 ${isEsports ? 'bg-[#12121e] border border-purple-500/20' : 'bg-white border border-gray-100 shadow-sm'}`} 
                                                            alt=""
                                                        />
                                                    )}
                                                    <span className="truncate">{team.name}</span>
                                                </div>
                                            </td>
                                            <td className={`px-2 py-3.5 text-center font-mono ${isEsports ? 'text-slate-300' : 'text-gray-600'}`}>{team.played}</td>
                                            <td className={`px-2 py-3.5 text-center font-mono ${isEsports ? 'text-slate-300' : 'text-gray-600'}`}>{team.won}</td>
                                            {sportType !== 'sets' && <td className={`px-2 py-3.5 text-center font-mono ${isEsports ? 'text-slate-300' : 'text-gray-600'}`}>{team.draw}</td>}
                                            <td className={`px-2 py-3.5 text-center font-mono ${isEsports ? 'text-slate-300' : 'text-gray-600'}`}>{team.lost}</td>
                                            <td className={`px-2 py-3.5 text-center font-mono ${isEsports ? 'text-slate-300' : 'text-gray-600'}`}>{team.gf}</td>
                                            <td className={`px-2 py-3.5 text-center font-mono ${isEsports ? 'text-slate-300' : 'text-gray-600'}`}>{team.ga}</td>
                                            <td className={`px-2 py-3.5 text-center font-mono font-bold ${isEsports ? 'text-slate-300' : 'text-gray-700'}`}>{team.gd}</td>
                                            <td className={`px-3 py-3.5 text-right font-black text-base font-mono ${
                                                isHighlighted 
                                                    ? (isEsports ? 'text-purple-300 drop-shadow-[0_0_5px_rgba(168,85,247,0.4)]' : 'text-indigo-700') 
                                                    : (isEsports ? 'text-slate-200' : 'text-gray-900')
                                            }`}>
                                                {team.points}
                                            </td>
                                        </tr>
                                    );
                                })}
                                {(!standings[poule.id] || standings[poule.id].length === 0) && (
                                    <tr>
                                        <td colSpan="10" className={`px-4 py-12 text-center text-xs italic font-semibold ${isEsports ? 'text-slate-500' : 'text-gray-400'}`}>
                                            {t('teams_page.no_teams_title')}
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            ))}

            {/* Sponsor Banner */}
            {sponsorImages.length > 0 && (
                <div className="mt-8 animate-fade-in">
                    <SponsorBanner sponsorUrls={sponsorImages} />
                </div>
            )}
        </div>
    );
};

export default PublicStandingsPage;
